import React, { useEffect, useRef, useState } from "react";
import AntModal from "../../AntModal";
import Icon from "../../Icon";
import {
  Title,
  Subtitle,
  DropZone,
  HiddenInput,
  DropZoneIcon,
  DropZonePrimary,
  DropZoneHelper,
  SelectedFile,
  ErrorText,
  Actions,
  UploadButton
} from "./styles";

const isMp3 = (file) =>
  file && (file.type === "audio/mpeg" || /\.mp3$/i.test(file.name));

const formatBytes = (bytes) => {
  if (!Number.isFinite(bytes)) return "";
  if (bytes < 1000) return `${bytes} B`;
  if (bytes < 1000 * 1000) return `${(bytes / 1000).toFixed(1)} KB`;
  return `${(bytes / (1000 * 1000)).toFixed(1)} MB`;
};


/**
 * Modal to upload mp3 file via file picker or dragndrop for similarity search.
 * Selection only — analyzing/success feedback lives in the search bar and a toast.
 */
const UploadModal = ({ open, onClose, onUpload }) => {
  const [file, setFile] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);

  // Reset internal state whenever the modal closes to start fresh
  useEffect(() => {
    if (!open) {
      setFile(null);
      setDragging(false);
      setError(null);
      if (inputRef.current) inputRef.current.value = "";
    }
  }, [open]);

  const handleFile = (picked) => {
    if (!picked) return;
    if (!isMp3(picked)) {
      setError("Only .mp3 files are supported.");
      setFile(null);
      return;
    }
    setError(null);
    setFile(picked);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!dragging) setDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();

    setDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();

    setDragging(false);
    const dropped = e.dataTransfer.files?.[0];
    handleFile(dropped);
  };

  const handleInputChange = (e) => {
    handleFile(e.target.files?.[0]);
  };

  const handleRemove = (e) => {
    e.preventDefault();
    e.stopPropagation();

    setFile(null);
    setError(null);

    if (inputRef.current) inputRef.current.value = "";
  };

  const handleSubmit = () => {
    if (!file || !onUpload) return;
    onUpload(file);
  };

  return (
    <AntModal modalState={{ visible: open, close: onClose }} width={540}>
      <Title>Upload a track</Title>
      <Subtitle>
        Find similar songs in our library by uploading your own mp3.
      </Subtitle>
      {file ? (
        <SelectedFile>
          <span className="file-icon">
            <Icon type="audio_file" size={28} inheritColor />
          </span>
          <div className="meta">
            <span className="name" title={file.name}>
              {file.name}
            </span>
            <span className="size">{formatBytes(file.size)}</span>
          </div>
          <button type="button" className="remove" onClick={handleRemove}>
            Remove
          </button>
        </SelectedFile>
      ) : (
        <DropZone
          $dragging={dragging}
          onDragOver={handleDragOver}
          onDragEnter={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <HiddenInput
            ref={inputRef}
            type="file"
            accept="audio/mpeg,.mp3"
            onChange={handleInputChange}
          />
          <DropZoneIcon>
            <Icon type="upload" size={40} inheritColor />
          </DropZoneIcon>
          <DropZonePrimary>
            Drag and drop, or <span className="browse">browse</span>
          </DropZonePrimary>
          <DropZoneHelper>.mp3 files only</DropZoneHelper>
        </DropZone>
      )}
      {error && <ErrorText>{error}</ErrorText>}
      <Actions>
        <button type="button" className="link" onClick={onClose}>
          Cancel
        </button>
        <UploadButton px={20} py={10} onClick={handleSubmit} disabled={!file}>
          Upload
        </UploadButton>
      </Actions>
    </AntModal>
  );
};

export default UploadModal;
